import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Paper,
  FormControl,
  FormLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useGuides } from "../hooks/useGuide";
import { useIntegration } from "../hooks/useIntegration";
import { useSelector } from "react-redux";
import { useUserMsp } from "../hooks/useUserMsp";
import { isMspOrgCustomer } from "../utils/mspOrgCustomer";
import { plainTextToHtml, hasHtmlTags } from "../utils/htmlUtils";
import LayeredAutocompleteSelect from "./LayeredAutocompleteSelect";

const GuideFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // id is undefined for add, present for edit
  const isEdit = Boolean(id);

  const {
    guides,
    selectedGuide,
    createGuide,
    updateGuide,
    deleteGuide,
    fetchGuides,
    fetchGuideById,
    isLoading: guidesLoading,
    selectedCustomerId,
    setCustomerSelection,
  } = useGuides();
  const {
    customers,
    getCustomers,
    isLoading: customersLoading,
  } = useIntegration();
  const user = useSelector((state: any) => state.auth.user);
  const { userMspInfo } = useUserMsp();

  // Filter customers by MSP ID and include 'root' customer (displayed as "All")
  const filteredCustomers = React.useMemo(() => {
    let arr = (customers || []).filter((c: any) => {
      return (
        c.name?.toLowerCase() === "root" ||
        String(c.msp_id) === String(userMspInfo?.msp_id)
      );
    });
    // Ensure 'All' (root) is first
    arr = [
      ...arr.filter((c: any) => c.name?.toLowerCase() === "root"),
      ...arr.filter((c: any) => c.name?.toLowerCase() !== "root" && !isMspOrgCustomer(c)),
    ];
    return arr;
  }, [customers, userMspInfo?.msp_id]);

  // Get preselected customer from location state (if any)
  // If "all" is passed, it means "All Customer" was selected, so don't preselect any customer
  let preselectedCustomer = location.state?.selectedCustomer;
  if (preselectedCustomer === "all") {
    preselectedCustomer = ""; // No customer preselected when "All Customer" is selected
  } else if (preselectedCustomer === undefined || preselectedCustomer === null || preselectedCustomer === "") {
    // If no preselected customer from location state, use selectedCustomerId or first customer
    // But check if selectedCustomerId is "All Customer" (root), if so, don't preselect
    const rootCustomer = filteredCustomers.find((c: any) => c.name?.toLowerCase() === "root");
    const isAllCustomerSelected = selectedCustomerId === rootCustomer?.id?.toString() || selectedCustomerId === "all";
    if (isAllCustomerSelected) {
      preselectedCustomer = "";
    } else {
      preselectedCustomer = selectedCustomerId || (filteredCustomers.length > 0 ? filteredCustomers[0].id : "");
    }
  }

  // Find guide if editing - prefer selectedGuide over guides array
  const initialGuide = isEdit
    ? selectedGuide || guides.find((g: any) => g.id === Number(id))
    : null;

  // Form state
  const [customerId, setCustomerId] = useState(
    isEdit 
      ? (initialGuide?.customer_id !== undefined && initialGuide?.customer_id !== null ? initialGuide.customer_id : "")
      : (preselectedCustomer !== undefined && preselectedCustomer !== null ? preselectedCustomer : "")
  );
  const [type, setType] = useState(initialGuide?.type || "");
  const [description, setDescription] = useState(
    initialGuide?.description || ""
  );
  const [stepsArray, setStepsArray] = useState<string[]>([]);
  const [validation, setValidation] = useState(initialGuide?.validation || "");
  const [platform, setPlatform] = useState(initialGuide?.platform || "");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Helper function to parse steps from HTML/text into an array
  const parseStepsToArray = (stepsText: string): string[] => {
    if (!stepsText) return [];
    
    let lines: string[] = [];
    
    // If it's HTML, handle <br /> tags explicitly
    if (hasHtmlTags(stepsText)) {
      // First, split by <br /> or <br> tags (case insensitive, with optional closing slash and whitespace)
      // This handles: <br>, <br/>, <br />, <BR>, etc.
      const brSplit = stepsText.split(/<br\s*\/?\s*>/i);
      
      // Process each part to extract text content
      lines = brSplit.map(part => {
        const trimmed = part.trim();
        if (!trimmed) return '';
        
        // Create a temporary div to extract text content (handles HTML entities and other tags)
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = trimmed;
        const text = (tempDiv.textContent || tempDiv.innerText || '').trim();
        return text;
      }).filter(line => line.length > 0);
      
      // If splitting by <br /> didn't produce multiple lines, try alternative approach
      if (lines.length <= 1) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = stepsText;
        const text = tempDiv.textContent || tempDiv.innerText || '';
        // Split by newlines (which might come from <br /> conversion)
        const newlineSplit = text.split(/\r?\n/);
        lines = newlineSplit.map(line => line.trim()).filter(line => line.length > 0);
      }
    } else {
      // Plain text - split by newlines
      lines = stepsText.split(/\r?\n/).map(line => line.trim()).filter(line => line.length > 0);
    }
    
    // Remove step numbers if present (e.g., "1. ", "2. ", etc.) and clean up
    const cleanedSteps = lines.map(line => {
      // Remove leading step numbers (e.g., "1. ", "2. ", "10. ", etc.)
      // This regex matches: one or more digits, followed by a dot, followed by optional whitespace
      return line.replace(/^\d+\.\s*/, '').trim();
    }).filter(step => step.length > 0);
    
    return cleanedSteps;
  };

  // Helper function to convert steps array back to HTML format
  const convertStepsArrayToHtml = (steps: string[]): string => {
    if (!steps || steps.length === 0) return '';
    
    // Filter out empty steps and create numbered list in HTML format
    const nonEmptySteps = steps.filter(step => step.trim().length > 0);
    if (nonEmptySteps.length === 0) return '';
    
    const numberedSteps = nonEmptySteps.map((step, index) => {
      const escapedStep = step
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return `${index + 1}. ${escapedStep}`;
    });
    
    // Join with <br /> tags
    return numberedSteps.join('<br />');
  };

  // Fetch customers when component mounts
  useEffect(() => {
    getCustomers();
  }, [getCustomers]);

  // Fetch specific guide when editing
  useEffect(() => {
    if (isEdit && id) {
      console.log("Fetching guide with ID:", id);
      fetchGuideById(id);
    }
  }, [isEdit, id, fetchGuideById]);

  // Update form fields when initialGuide changes (after guides are loaded)
  useEffect(() => {
    if (isEdit && initialGuide) {
      console.log("Setting form fields from guide:", initialGuide);
      setCustomerId(initialGuide.customer_id !== undefined && initialGuide.customer_id !== null ? initialGuide.customer_id : "");
      setType(initialGuide.type || "");
      setDescription(initialGuide.description || "");
      // Parse steps into array
      const stepsValue = initialGuide.steps || "";
      const parsedSteps = parseStepsToArray(stepsValue);
      // Always ensure at least 3 steps (pad with empty strings if needed)
      while (parsedSteps.length < 3) {
        parsedSteps.push("");
      }
      setStepsArray(parsedSteps);
      setValidation(initialGuide.validation || "");
      setPlatform(initialGuide.platform || "");
    } else if (!isEdit) {
      // For new guides, initialize with 3 empty steps
      setStepsArray(["", "", ""]);
    }
  }, [isEdit, initialGuide]);

  // Update customerId when filteredCustomers change (for new guide)
  // Don't auto-select if preselectedCustomer is empty (meaning "All Customer" was selected)
  useEffect(() => {
    if (!isEdit && filteredCustomers.length > 0 && (customerId === "" || customerId === null || customerId === undefined)) {
      // Only auto-select if preselectedCustomer is not empty
      if (preselectedCustomer !== "" && preselectedCustomer !== null && preselectedCustomer !== undefined) {
        setCustomerId(preselectedCustomer);
      }
    }
  }, [isEdit, filteredCustomers, customerId, preselectedCustomer]);

  // Update Redux state when customer selection changes
  useEffect(() => {
    if (customerId) {
      setCustomerSelection(customerId);
    }
  }, [customerId, setCustomerSelection]);

  // Check if we're still loading data
  const isLoading = guidesLoading || customersLoading;

  // Check if we have the guide data when editing
  const hasGuideData = !isEdit || (isEdit && initialGuide);

  // Debug logging
  useEffect(() => {
    console.log("GuideFormPage state:", {
      isEdit,
      id,
      selectedGuide,
      guides: guides?.length,
      customers: customers?.length,
      customerId,
      isLoading,
    });
  }, [isEdit, id, selectedGuide, guides, customers, customerId, isLoading]);

  const handleSave = () => {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, "0");
    const dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
      now.getDate()
    )} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(
      now.getSeconds()
    )}`;
    // Convert steps array back to HTML format
    const stepsHtml = convertStepsArrayToHtml(stepsArray);
    const data = {
      customer_id: customerId,
      type,
      platform,
      description,
      steps: stepsHtml,
      validation,
      msp_id: userMspInfo?.msp_id,
    };
    if (isEdit) {
      updateGuide({ ...initialGuide, ...data, last_updated: dateStr });
      navigate(`/guides/${id}`);
    } else {
      createGuide({
        ...data,
        created_by: user?.name || user?.email || "Unknown",
        date_added: dateStr,
        last_updated: dateStr,
      });
      navigate("/guides", { state: { selectedCustomer: selectedCustomerId } });
    }
  };

  const handleAddStep = () => {
    setStepsArray([...stepsArray, ""]);
  };

  const handleRemoveStep = (index: number) => {
    // Always keep at least 3 steps
    if (stepsArray.length > 3) {
      const newSteps = stepsArray.filter((_: string, i: number) => i !== index);
      setStepsArray(newSteps);
    }
  };

  const handleStepChange = (index: number, value: string) => {
    const newSteps = [...stepsArray];
    newSteps[index] = value;
    setStepsArray(newSteps);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (dropIndex: number) => {
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newSteps = [...stepsArray];
    const draggedStep = newSteps[draggedIndex];
    
    // Remove the dragged item
    newSteps.splice(draggedIndex, 1);
    
    // Insert it at the new position
    newSteps.splice(dropIndex, 0, draggedStep);
    
    setStepsArray(newSteps);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleDelete = () => {
    if (id) {
      deleteGuide(id);
      navigate("/guides", { state: { selectedCustomer: selectedCustomerId } });
    }
  };

  // Show loading state if we're still fetching data
  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          mx: 1,
          my: 1,
          p: { xs: 2, sm: 3, md: 4 },
          background: "#FFF",
          borderRadius: 1,
          fontFamily: "var(--theo-font-sans)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IconButton onClick={() => navigate("/guides", { state: { selectedCustomer: selectedCustomerId } })}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ ml: 1 }}>
            {isEdit ? "Edit a Guide" : "Add a Guide"}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography>Loading...</Typography>
        </Box>
      </Paper>
    );
  }

  // Show error state if editing but no guide data found
  if (isEdit && !hasGuideData) {
    return (
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          mx: 1,
          my: 1,
          p: { xs: 2, sm: 3, md: 4 },
          background: "#FFF",
          borderRadius: 1,
          fontFamily: "var(--theo-font-sans)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IconButton onClick={() => navigate("/guides", { state: { selectedCustomer: selectedCustomerId } })}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ ml: 1 }}>
            Edit a Guide
          </Typography>
        </Box>
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography color="error">
            Guide not found or still loading...
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate("/guides", { state: { selectedCustomer: selectedCustomerId } })}
            sx={{ mt: 2 }}
          >
            Back to Guides
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        // maxWidth: 900,
        mx: 1,
        my: 1,
        p: { xs: 2, sm: 3, md: 4 },
        background: "#FFF",
        borderRadius: 1,
        // boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        // border: '1px solid #E0E0E0',
        fontFamily: "var(--theo-font-sans)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <IconButton onClick={() => navigate("/guides", { state: { selectedCustomer: selectedCustomerId } })}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ ml: 1 }}>
          {isEdit ? "Edit a Guide" : "Add a Guide"}
        </Typography>
      </Box>
      {/* Created By Display (only for edit mode) */}
      {isEdit && initialGuide?.created_by && (
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, px: 1 }}>
          <Typography sx={{ fontWeight: 500, fontFamily: "var(--theo-font-sans)", color: "#838383", fontSize: 14 }}>
            Created by:
          </Typography>
          <Typography sx={{ ml: 1, fontFamily: "var(--theo-font-sans)", fontSize: 14 }}>
            {initialGuide.created_by}
          </Typography>
        </Box>
      )}
      {/* Customer Selector */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography sx={{ width: 140, fontWeight: 500, fontFamily: "var(--theo-font-sans)" }}>
          Customer
        </Typography>
        <LayeredAutocompleteSelect
          label="Customer"
          value={String(customerId ?? "")}
          onChange={setCustomerId}
          disabled={isLoading}
          options={filteredCustomers.map((c: any) => ({
            value: String(c.id),
            label: c.name?.toLowerCase() === "root" ? "All Customer" : c.name,
          }))}
          noOptionsText="No customers available"
          sx={{ flex: 1, minWidth: 300 }}
          textFieldSx={{ "& .MuiInputBase-root": { fontFamily: "var(--theo-font-sans)" } }}
        />
      </Box>

      {/* Type Selector */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography sx={{ width: 140, fontWeight: 500, fontFamily: "var(--theo-font-sans)" }}>
          Type
        </Typography>
        <LayeredAutocompleteSelect
          label="Type"
          value={type}
          onChange={setType}
          disabled={isLoading}
          options={[
            { value: "Troubleshooting", label: "Troubleshooting" },
            { value: "Procedure", label: "Procedure" },
            { value: "Skill", label: "Skill" },
          ]}
          noOptionsText="No types available"
          sx={{ flex: 1, minWidth: 300 }}
          textFieldSx={{ "& .MuiInputBase-root": { fontFamily: "var(--theo-font-sans)" } }}
        />
      </Box>

      {/* Platform Selector */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography sx={{ width: 140, fontWeight: 500, fontFamily: "var(--theo-font-sans)" }}>
          Platform
        </Typography>
        <LayeredAutocompleteSelect
          label="Platform"
          value={platform}
          onChange={setPlatform}
          disabled={isLoading}
          options={[
            { value: "None", label: "None" },
            { value: "Win", label: "Windows" },
            { value: "Mac", label: "Mac" },
            { value: "Linux", label: "Linux" },
          ]}
          noOptionsText="No platforms available"
          sx={{ flex: 1, minWidth: 300 }}
          textFieldSx={{ "& .MuiInputBase-root": { fontFamily: "var(--theo-font-sans)" } }}
        />
      </Box>

      {/* Problem Description */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography sx={{ width: 140, fontWeight: 500, fontFamily: "var(--theo-font-sans)" }}>
          Problem Description
        </Typography>
        <TextField
          label=""
          placeholder="Describe the problem..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={isLoading}
          fullWidth
          sx={{ fontFamily: "var(--theo-font-sans)", flex: 1, minWidth: 300 }}
          InputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
        />
      </Box>

      {/* Steps */}
      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
        <Typography
          sx={{ width: 140, fontWeight: 500, fontFamily: "var(--theo-font-sans)", pt: 1 }}
        >
          Steps
        </Typography>
        <FormControl sx={{ flex: 1, minWidth: 300 }}>
          <Box
            sx={{
              backgroundColor: "#FAFAFA",
              padding: 2,
              borderRadius: 1,
              border: "1px solid #E0E0E0",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {stepsArray.map((step, index) => (
              <Box
                key={index}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(index)}
                onDragEnd={handleDragEnd}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "move",
                  opacity: draggedIndex === index ? 0.5 : 1,
                  "&:hover": {
                    backgroundColor: draggedIndex === null ? "rgba(0, 0, 0, 0.02)" : "transparent",
                  },
                }}
              >
                <IconButton
                  sx={{
                    cursor: "grab",
                    color: "#999",
                    padding: 0.5,
                    "&:active": {
                      cursor: "grabbing",
                    },
                  }}
                  disabled={isLoading}
                >
                  <DragIndicatorIcon />
                </IconButton>
                <Typography
                  sx={{
                    minWidth: 30,
                    fontWeight: 500,
                    fontFamily: "var(--theo-font-sans)",
                    color: "#666",
                  }}
                >
                  {index + 1}.
                </Typography>
                <TextField
                  value={step}
                  onChange={(e) => handleStepChange(index, e.target.value)}
                  disabled={isLoading}
                  placeholder={`Step ${index + 1}...`}
                  fullWidth
                  onDragStart={(e) => e.stopPropagation()}
                  sx={{
                    fontFamily: "var(--theo-font-sans)",
                    backgroundColor: "#fff",
                    "& .MuiOutlinedInput-root": {
                      fontFamily: "var(--theo-font-sans)",
                    },
                  }}
                  InputProps={{
                    style: { fontFamily: "var(--theo-font-sans)" },
                  }}
                />
                {stepsArray.length > 3 && (
                  <IconButton
                    onClick={() => handleRemoveStep(index)}
                    disabled={isLoading}
                    sx={{
                      color: "#d32f2f",
                      "&:hover": {
                        backgroundColor: "rgba(211, 47, 47, 0.04)",
                      },
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddStep}
              disabled={isLoading}
              variant="outlined"
              sx={{
                mt: 1,
                fontFamily: "var(--theo-font-sans)",
                alignSelf: "flex-start",
              }}
            >
              Add Step
            </Button>
          </Box>
        </FormControl>
      </Box>

      {/* Validation */}
      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
        <Typography
          sx={{ width: 140, fontWeight: 500, fontFamily: "var(--theo-font-sans)", pt: 1 }}
        >
          Validation
        </Typography>
        <FormControl sx={{ flex: 1, minWidth: 300 }}>
          <TextareaAutosize
            minRows={3}
            maxRows={7}
            value={validation}
            onChange={(e) => setValidation(e.target.value)}
            disabled={isLoading}
            placeholder="How do you validate the solution works?"
            style={{
              fontFamily: "var(--theo-font-sans)",
              fontSize: 16,
              width: "100%",
              borderRadius: 6,
              border: "1px solid #ccc",
              padding: 12,
              resize: "vertical",
              maxHeight: 140,
              overflowY: "auto",
              background: "#fff",
              boxSizing: "border-box",
              opacity: isLoading ? 0.6 : 1,
            }}
          />
        </FormControl>
      </Box>
      <Box sx={{ display: "flex", gap: 2, mt: 2, justifyContent: "space-between" }}>
        <Box>
          {isEdit && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={isLoading}
            >
              Delete
            </Button>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={customerId === "" || customerId === null || customerId === undefined || !type || !description || isLoading}
          >
            {isLoading ? "Loading..." : "Save"}
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => navigate(isEdit ? `/guides/${id}` : "/guides")}
          >
            Cancel
          </Button>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title" sx={{ fontFamily: "var(--theo-font-sans)" }}>
          Delete Guide
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description" sx={{ fontFamily: "var(--theo-font-sans)" }}>
            Are you sure you want to delete this guide? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontFamily: "var(--theo-font-sans)" }}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              handleDelete();
            }} 
            color="error" 
            variant="contained"
            sx={{ fontFamily: "var(--theo-font-sans)" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default GuideFormPage;
